import React, { useEffect, useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'

const FADE_OUT_DURATION_MS = 250

type UndoToastProps = {
  message: string
  onUndo: () => void
  onClose: () => void
  autoHideDurationMs?: number
  showCloseButton?: boolean
  title?: string
  variant?: 'default' | 'warning'
  position?: 'bottom-left' | 'bottom-right'
  timestampLabel?: string
}

export const UndoToast: React.FC<UndoToastProps> = ({
  message,
  onUndo,
  onClose,
  autoHideDurationMs,
  showCloseButton = true,
  title,
  variant = 'default',
  position = 'bottom-left',
  timestampLabel,
}) => {
  const [isClosing, setIsClosing] = useState(false)

  useEffect(() => {
    setIsClosing(false)

    if (autoHideDurationMs === undefined) {
      return undefined
    }

    const fadeTimeoutId = window.setTimeout(() => {
      setIsClosing(true)
    }, autoHideDurationMs)

    const closeTimeoutId = window.setTimeout(() => {
      onClose()
    }, autoHideDurationMs + FADE_OUT_DURATION_MS)

    return () => {
      window.clearTimeout(fadeTimeoutId)
      window.clearTimeout(closeTimeoutId)
    }
  }, [autoHideDurationMs, message, onClose])

  const positionClass = position === 'bottom-right' ? 'right-6' : 'left-6'
  const opacityClass = isClosing ? 'opacity-0' : 'opacity-100'

  if (variant === 'warning') {
    return (
      <div
        role="status"
        className={`fixed ${positionClass} bottom-6 z-50 w-[380px] max-w-[calc(100vw-48px)] rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-opacity ease-out duration-[250ms] ${opacityClass}`}
        data-state={isClosing ? 'closing' : 'open'}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            {title ? <p className="font-semibold text-gray-900">{title}</p> : null}
            <p className="mt-0.5 text-gray-700">{message}</p>
          </div>
          {showCloseButton ? (
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 cursor-pointer rounded p-0.5 text-[#1D5176] hover:bg-amber-100"
              aria-label="Close toast"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <div className="mt-2 flex items-center justify-between pl-8">
          <span className="text-xs text-gray-500">{timestampLabel}</span>
          <button
            type="button"
            onClick={onUndo}
            className="cursor-pointer text-sm font-semibold text-[#1D5176] hover:underline"
          >
            Undo
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`fixed ${positionClass} bottom-6 z-50 flex items-center gap-4 bg-[#1D5176] text-white py-3 px-4 rounded-md shadow-[0_4px_12px_rgba(0,0,0,0.15)] text-sm min-w-[300px] justify-between transition-opacity ease-out duration-[250ms] ${opacityClass}`}
      data-state={isClosing ? 'closing' : 'open'}
    >
      <span>
        {title ? <strong className="mr-1 font-semibold">{title}</strong> : null}
        {message}
      </span>
      <div className="flex items-center gap-3">
        <button
          onClick={onUndo}
          className="bg-white text-[#1E293B] border-none rounded py-1 px-3 font-semibold text-[13px] cursor-pointer"
        >
          Undo
        </button>
        {showCloseButton ? (
          <button
            onClick={onClose}
            className="bg-transparent border-none text-[#94A3B8] cursor-pointer text-base leading-none p-0 flex items-center"
            aria-label="Close toast"
          >
            &times;
          </button>
        ) : null}
      </div>
    </div>
  )
}
